import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('nx_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('nx_token');
      localStorage.removeItem('nx_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('nx_token', res.data.token);
    localStorage.setItem('nx_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, tenantType = 'brand') => {
    const res = await authAPI.register({ name, email, password, tenant_type: tenantType });
    localStorage.setItem('nx_token', res.data.token);
    localStorage.setItem('nx_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  // Free Evaluation Workspace -- flips the tenant's plan server-side,
  // then re-runs the same /auth/me read checkAuth already uses so
  // `user.plan` (and every gate built on it) updates from one real
  // source of truth instead of being patched locally.
  const activateFullWorkspace = async () => {
    await authAPI.activateFullWorkspace();
    await checkAuth();
  };

  const logout = async () => {
    // AF-3.17.1 Section 4: revoke the token server-side first (a
    // per-token denylist entry) -- if the network call fails, the user
    // must still be logged out of THIS browser; the server-side
    // revocation attempt is a best-effort addition, not the only line
    // of defense (the token's own expiry is still the fallback).
    try {
      await authAPI.logout();
    } catch {
      // Already-invalid/expired tokens and offline/network failures
      // must never block clearing the local session.
    }
    localStorage.removeItem('nx_token');
    localStorage.removeItem('nx_user');
    setUser(null);
  };

  // AF-3.17.1 Section 1: "owner" sits above "admin" in the role
  // hierarchy (the tenant's original creator) -- these UX-only capability
  // flags must include it, or an owner account would incorrectly see
  // admin/editor-only UI hidden. This is a display convenience only;
  // the real authorization boundary is server-side (require_role on
  // every route), never this flag.
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isEditor = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'editor';
  const isViewer = true; // everyone can view

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, activateFullWorkspace, isAdmin, isEditor, isViewer }}>
      {children}
    </AuthContext.Provider>
  );
};
